import {FC, useState} from 'react';
import {Alert, Button, TextField, Typography} from '@mui/material';
import {useForm} from 'react-hook-form';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import Modal from 'components/Modal/Modal';
import {DISABLE_COUNTRY} from 'graphql/absences/Mutations';

interface DisableCountryModalProps {
  open: boolean;
  countryCode: string;
  countryName: string;
  onClose: () => void;
  onSuccess: (affectedUsersCount: number) => void;
}

interface DisableFormValues {
  reason: string;
}

/**
 * Two-step modal for disabling a country configuration.
 * Step 1 collects the reason (minimum 10 characters).
 * Step 2 shows a confirmation summary before executing the mutation.
 *
 * @param open - Whether the modal is visible.
 * @param countryCode - ISO code of the country to disable.
 * @param countryName - Human-readable country name for display.
 * @param onClose - Callback invoked when the modal is dismissed.
 * @param onSuccess - Callback invoked on success, receives the affectedUsersCount.
 */
const DisableCountryModal: FC<DisableCountryModalProps> = ({
  open,
  countryCode,
  countryName,
  onClose,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {register, watch, reset} = useForm<DisableFormValues>({
    defaultValues: {reason: ''},
  });

  const reason = watch('reason');
  const isReasonValid = reason.trim().length >= 10;

  const [disableCountry] = useMutation(DISABLE_COUNTRY);

  const handleClose = () => {
    reset();
    setStep(1);
    setErrorMessage(null);
    onClose();
  };

  const handleNext = () => {
    if (isReasonValid) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const {data} = await disableCountry({
        variables: {
          countryCode,
          reason: reason.trim(),
        },
      });
      reset();
      setStep(1);
      onSuccess(data.disableCountry.affectedUsersCount);
    } catch (err: unknown) {
      // Show backend error message or fallback to generic error
      const message = err instanceof Error ? err.message : undefined;
      setErrorMessage(message ?? t('leaves.DisableCountryModal.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('leaves.DisableCountryModal.title')}
      onClose={handleClose}
      small
    >
      {step === 1 ? (
        <div className="flex flex-col gap-4">
          <Typography variant="subtitle1">
            {t('leaves.DisableCountryModal.step1Title')}
          </Typography>
          <TextField
            label={t('leaves.DisableCountryModal.reasonLabel')}
            placeholder={t('leaves.DisableCountryModal.reasonPlaceholder')}
            multiline
            rows={3}
            fullWidth
            {...register('reason')}
          />
          {reason.length > 0 && !isReasonValid && (
            <Typography variant="caption" color="error">
              {t('leaves.DisableCountryModal.reasonMinLength')}
            </Typography>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleClose}>
              {t('leaves.DisableCountryModal.cancelButton')}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isReasonValid}
            >
              {t('leaves.DisableCountryModal.nextButton')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Typography variant="subtitle1">
            {t('leaves.DisableCountryModal.step2Title')}
          </Typography>
          <Typography variant="body2">
            {t('leaves.DisableCountryModal.confirmDescription')}
          </Typography>
          <Typography variant="body2">{countryName}</Typography>
          {errorMessage && (
            <Alert severity="error" sx={{mt: 1}}>
              {errorMessage}
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleBack}>
              {t('leaves.DisableCountryModal.cancelButton')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {t('leaves.DisableCountryModal.submitButton')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DisableCountryModal;
